import axios from 'axios'
import { store } from '../../../store/store'
import type { RootState } from '../../../store/store'
import { forceLogout } from '../../../store/slices/authSlice'
import { navigateToLogin } from './core/NavigationHelper'
import { userTimezone } from '../user/helper/dateTimeHelpers'
import { getCurrentApiUrl } from '../../utils/backendConfig'
import { refreshDebouncer } from '../../utils/refreshDebouncer'
import { shouldSkipAuthCheck, getAuthCheckInterval, shouldSkipAuthCheckByHeaders } from './authInterceptorConfig'
import { shouldRunAuthInterceptor, shouldRunBackgroundAuthCheck, logAuthDebug } from '../../utils/authDebugConfig'

const getApiUrl = () => getCurrentApiUrl()

// Background auth check that doesn't trigger Redux state updates
// This prevents the refresh loop caused by frequent state changes
const backgroundAuthCheck = async (token: string) => {
  try {
    const response = await axios.get(`${getApiUrl()}/auth/me`, {
      headers: {
        'auth-token': token,
        'Authorization': `Bearer ${token}`,
        'Accept': 'application/json',
        'skipAuth': 'true' // Prevent this call from triggering the interceptor
      },
      timeout: 5000
    })
    
    const userData = response.data?.data || response.data?.user || response.data
    
    // Only take action if user is disabled or unauthorized
    // Don't update Redux state for normal responses to prevent refresh loops
    if (userData && (userData.status === 0 || userData.status === "0" || userData.status === false)) {
      console.log('🚫 Background auth check: User disabled, forcing logout')
      store.dispatch(forceLogout())
      setTimeout(() => {
        navigateToLogin()
      }, 100)
    }
    
    return userData
  } catch (error: any) {
    // Only handle critical errors (401) that require logout
    if (error.response?.status === 401) {
      console.log('🔐 Background auth check: Unauthorized, forcing logout')
      store.dispatch(forceLogout())
      setTimeout(() => {
        navigateToLogin()
      }, 100)
    }
    // For other errors, silently continue - don't block the original API call
    return null
  }
}

export function setupAxiosWithRedux(axiosInstance: any) {
  axiosInstance.defaults.headers.Accept = 'application/json'
  axiosInstance.defaults.baseURL = getApiUrl()
  
  axiosInstance.interceptors.request.use(
    async (config: any) => {
      const state = store.getState()
      const authState = state.auth
      // Use Laravel token if available, otherwise fall back to regular token
      const token = authState.laravel_token || authState.token
      
      if (token && config.headers) {
        config.headers['auth-token'] = token
        config.headers['Authorization'] = `Bearer ${token}`
      }
      
      // Add timezone to query parameters
      if (config.params) {
        config.params['timezone'] = userTimezone // Append to query parameters if any
      } else {
        config.params = {timezone: userTimezone} // If no params, create new
      }
      
      // Call /auth/me before API requests to keep user state updated
      // Skip for auth endpoints and if user is not authenticated
      const skipAuthCheck = shouldSkipAuthCheck(config.url) || shouldSkipAuthCheckByHeaders(config.headers)
      
      if (token && !skipAuthCheck && authState.isAuthenticated && shouldRunAuthInterceptor()) {
        // Get appropriate interval based on the URL
        const interval = getAuthCheckInterval(config.url)
        
        // Use debouncer to prevent excessive calls
        if (refreshDebouncer.shouldAllowRefresh('authMeCheck', interval)) {
          try {
            logAuthDebug(`Calling /auth/me before ${config.url} (interval: ${interval}ms)`)
            // Call /auth/me in background without blocking the request
            // This prevents the refresh loop caused by Redux state changes
            if (shouldRunBackgroundAuthCheck()) {
              backgroundAuthCheck(token).catch(error => {
                console.warn('Background auth check failed:', error)
              })
            }
          } catch (error) {
            console.warn('Failed to start background auth check:', error)
          }
        }
      }
      
      return config
    },
    (error: any) => {
      return Promise.reject(error)
    }
  )
  
  axiosInstance.interceptors.response.use(
    (response: any) => {
      // Check if response contains user data and user is disabled
      const userData = response?.data?.data?.user || response?.data?.user
      const state: RootState = store.getState()
      const currentUserUUID = state.auth.user?.uuid
      // Only force logout if the disabled user is the current user
      if (
        userData &&
        (userData.status === 0 || userData.status === "0" || userData.status === false) &&
        userData.uuid &&
        userData.uuid === currentUserUUID
      ) {
        console.log('🚫 User disabled detected in response, forcing logout')
        store.dispatch(forceLogout())
        setTimeout(() => {
          navigateToLogin()
        }, 100)
        return Promise.reject(new Error('User account has been disabled'))
      }
      return response
    },
    async (error: any) => {
      const { response } = error
      
      if (response?.status === 401) {
        console.log('🔐 401 Unauthorized detected, forcing logout')
        store.dispatch(forceLogout())
        setTimeout(() => {
          navigateToLogin()
        }, 100)
      }
      
      return Promise.reject(error)
    }
  )
}
